import React, { useState } from 'react';
import { addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, isBefore, isSameMonth, parseISO, startOfDay, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface DateRangeCalendarProps {
  mode?: 'range' | 'single';
  start: string | null;
  end?: string | null;
  /** Dates known to be unavailable. Empty until the backend exposes availability. */
  unavailable?: string[];
  onChange: (start: string | null, end: string | null) => void;
}
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const toKey = (date: Date) => format(date, 'yyyy-MM-dd');
export function DateRangeCalendar({
  mode = 'range',
  start,
  end = null,
  unavailable = [],
  onChange
}: DateRangeCalendarProps) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [hovered, setHovered] = useState<string | null>(null);
  const today = startOfDay(new Date());
  const months = [cursor, addMonths(cursor, 1)];
  const canGoBack = isBefore(startOfMonth(today), cursor);
  const rangeEnd = mode === 'range' ? end ?? (start && hovered && hovered > start ? hovered : null) : null;
  function handleClick(key: string) {
    if (mode === 'single') {
      onChange(key, null);
      return;
    }
    if (!start || end || key <= start) {
      onChange(key, null);
      return;
    }
    const blocked = unavailable.some(day => day >= start && day < key);
    onChange(blocked ? key : start, blocked ? null : key);
  }
  return <div data-id="element-84" data-mp="2anhekc439heh">
      <div className="mb-5 flex items-center justify-between" data-id="element-85" data-mp="2anr6444s7wma">
        <button type="button" onClick={() => canGoBack && setCursor(subMonths(cursor, 1))} disabled={!canGoBack} aria-label="Previous month" className="inline-flex h-9 w-9 items-center justify-center rounded-full text-forest-600 transition-colors duration-150 ease-premium hover:bg-forest-50 disabled:opacity-30 disabled:hover:bg-transparent" data-id="element-86" data-mp="2pjgsw4hskds2">
          <ChevronLeftIcon size={17} data-id="element-87" data-mp="6w1n3n54mtux" />
        </button>
        <p className="font-display text-base text-forest-700" data-id="element-88" data-mp="6zfs7356dh62">
          {format(months[0], 'MMMM yyyy')}
          <span className="hidden sm:inline" data-id="element-89" data-mp="gv990ypxfwtn"> — {format(months[1], 'MMMM yyyy')}</span>
        </p>
        <button type="button" onClick={() => setCursor(addMonths(cursor, 1))} aria-label="Next month" className="inline-flex h-9 w-9 items-center justify-center rounded-full text-forest-600 transition-colors duration-150 ease-premium hover:bg-forest-50" data-id="element-90" data-mp="luc2rn0gigmb">
          <ChevronRightIcon size={17} data-id="element-91" data-mp="3uafec3uowpfx" />
        </button>
      </div>

      <div className="grid gap-8 sm:grid-cols-2" data-id="element-92" data-mp="3u82m8xv7wyp5">
        {months.map((month, monthIndex) => <div key={month.toISOString()} className={monthIndex === 1 ? 'hidden sm:block' : ''} data-id="element-93" data-mp="2xkppvbicxqp" data-mpi={monthIndex}>
            <div className="grid grid-cols-7 gap-y-1" role="grid" aria-label={format(month, 'MMMM yyyy')} data-id="element-94" data-mp="31l86ufd9cpij">
              {WEEKDAYS.map((day, index) => <div key={`${day}-${index}`} className="pb-2 text-center text-[11px] uppercase text-gold-700" aria-hidden="true" data-id="element-95" data-mp="1npqcvovybqdp" data-mpi={index}>
                  {day}
                </div>)}
              {eachDayOfInterval({
            start: startOfWeek(startOfMonth(month)),
            end: endOfWeek(endOfMonth(month))
          }).map(day => {
            const key = toKey(day);
            if (!isSameMonth(day, month)) return <div key={key} aria-hidden="true" data-id="element-96" data-mp="14qufy5spkxw6" />;
            const past = isBefore(day, today);
            const blocked = unavailable.includes(key);
            const isStart = start === key;
            const isEnd = end === key;
            const inRange = Boolean(start && rangeEnd && key > start && key < rangeEnd);
            return <div key={key} className={`flex justify-center py-0.5 ${inRange ? 'bg-gold-100' : ''} ${isStart && rangeEnd ? 'rounded-l-full bg-gold-100' : ''} ${isEnd || rangeEnd === key && !end ? 'rounded-r-full bg-gold-100' : ''}`} data-id="element-97" data-mp="m03b9hjo1vo4">
                    <button type="button" disabled={past || blocked} onMouseEnter={() => setHovered(key)} onMouseLeave={() => setHovered(null)} onClick={() => handleClick(key)} aria-label={`${format(day, 'EEEE d MMMM yyyy')}${blocked ? ' — unavailable' : ''}`} aria-pressed={isStart || isEnd} className={`flex h-9 w-9 items-center justify-center rounded-full text-[13px] transition-colors duration-150 ease-premium active:scale-95 ${isStart || isEnd ? 'bg-forest-700 text-ivory-100' : blocked ? 'cursor-not-allowed text-forest-200 line-through' : past ? 'cursor-not-allowed text-forest-200' : 'text-ink hover:bg-gold-100'}`} data-id="element-98" data-mp="2m1b1mcbis3dq">
                      {format(day, 'd')}
                    </button>
                  </div>;
          })}
            </div>
          </div>)}
      </div>

      <p className="mt-4 min-h-[20px] text-[13px] text-forest-600" aria-live="polite" data-id="element-99" data-mp="1h1usi2neqav4">
        {mode === 'range' && start && !end && `Check-in ${format(parseISO(start), 'EEE d MMM')} — now choose your checkout date.`}
        {mode === 'range' && start && end && `${format(parseISO(start), 'EEE d MMM')} → ${format(parseISO(end), 'EEE d MMM yyyy')}`}
        {mode === 'single' && start && `Shoot date: ${format(parseISO(start), 'EEEE d MMMM yyyy')}`}
      </p>
    </div>;
}